import { useEffect, useMemo, useState } from 'react';

import {
  getChangePlanPreviewRequest,
  type ChangePlanPreviewResponse,
} from 'services/subscriptions';
import {
  SubscriptionFrequency,
  SubscriptionStatus,
  UserSubscription,
} from 'types';

export type CheckoutProration = {
  creditAmount: number;
  totalDueToday: number;
  planLinePrice: number;
  remainingCreditBalance: number;
  currency?: string;
  renewalDate?: string | null;
  fromPlanName?: string;
  planChangeDirection?: 'upgrade' | 'downgrade' | null;
};

type UseCheckoutProrationParams = {
  displayPayButton: boolean;
  checkoutPlanId: string | undefined;
  activeSubscription: UserSubscription | null | undefined;
  frequency: string | null;
  currentPrice: number;
  isSubscriptionPending?: boolean;
};

const roundMoney = (value: number) => Math.round(value * 100) / 100;

type PlanTierComparable = {
  maxCommunities?: number | null;
  maxMembers?: number | null;
  yearlyValue?: number | null;
  monthlyValue?: number | null;
};

const comparePlanTier = (
  newPlan: PlanTierComparable | null | undefined,
  currentPlan: PlanTierComparable | null | undefined,
  frequency: SubscriptionFrequency,
): 'upgrade' | 'downgrade' | null => {
  if (!newPlan || !currentPlan) return null;

  const newCommunities = newPlan.maxCommunities ?? 0;
  const currentCommunities = currentPlan.maxCommunities ?? 0;
  if (newCommunities !== currentCommunities) {
    return newCommunities > currentCommunities ? 'upgrade' : 'downgrade';
  }

  const newMembers = newPlan.maxMembers ?? 0;
  const currentMembers = currentPlan.maxMembers ?? 0;

  if (newMembers !== currentMembers) {
    return newMembers > currentMembers ? 'upgrade' : 'downgrade';
  }

  const newPrice =
    frequency === SubscriptionFrequency.YEAR
      ? newPlan.yearlyValue ?? 0
      : newPlan.monthlyValue ?? 0;

  const currentPrice =
    frequency === SubscriptionFrequency.YEAR
      ? currentPlan.yearlyValue ?? 0
      : currentPlan.monthlyValue ?? 0;

  if (newPrice !== currentPrice) {
    return newPrice > currentPrice ? 'upgrade' : 'downgrade';
  }

  return null;
};

const isPaidActiveSubscription = (
  active: UserSubscription | null | undefined,
): boolean => {
  if (!active?.id) return false;
  if (active.status === SubscriptionStatus.CANCELLED) return false;
  if (active.planId === SubscriptionStatus.TRIAL) return false;
  return true;
};

const planPriceFromPreview = (
  newPlan: ChangePlanPreviewResponse['newPlan'],
  frequency: SubscriptionFrequency,
  fallback: number,
): number => {
  if (!newPlan) return fallback;
  const yearly = newPlan.yearlyValue;
  const monthly = newPlan.monthlyValue;
  if (frequency === SubscriptionFrequency.YEAR && yearly != null) {
    return typeof yearly === 'number' ? yearly : fallback;
  }
  if (frequency === SubscriptionFrequency.MONTH && monthly != null) {
    return typeof monthly === 'number' ? monthly : fallback;
  }
  return fallback;
};

const mapPreviewToProration = (
  data: ChangePlanPreviewResponse,
  frequency: SubscriptionFrequency,
  fallbackLinePrice: number,
): CheckoutProration => {
  const fromPlanName =
    data.currentPlan?.productName || data.currentPlan?.name || undefined;
  const planChangeDirection = comparePlanTier(
    data.newPlan,
    data.currentPlan,
    frequency,
  );

  const breakdown = data.pricingBreakdown;
  if (breakdown) {
    const creditAmount =
      breakdown.proratedCreditAmount != null
        ? Math.abs(breakdown.proratedCreditAmount)
        : 0;

    const remainingCreditBalance =
      breakdown.proratedBalance != null && breakdown.proratedBalance !== 0
        ? Math.abs(breakdown.proratedBalance)
        : 0;

    return {
      creditAmount,
      totalDueToday: Math.max(0, breakdown.amountDue),
      planLinePrice: breakdown.actualPlanPrice,
      remainingCreditBalance,
      currency: breakdown.currency ?? data.currency,
      renewalDate: data.renewalDate ?? null,
      fromPlanName,
      planChangeDirection,
    };
  }

  const planLinePrice = planPriceFromPreview(
    data.newPlan,
    frequency,
    fallbackLinePrice,
  );
  const totalDueToday = Math.max(0, data.amountDue);
  const creditAmount =
    data.creditAmount != null
      ? data.creditAmount
      : roundMoney(Math.max(0, planLinePrice - totalDueToday));

  return {
    creditAmount,
    totalDueToday,
    planLinePrice:
      data.newPlanFullPrice != null ? data.newPlanFullPrice : planLinePrice,
    remainingCreditBalance: 0,
    currency: data.currency,
    renewalDate: data.renewalDate ?? null,
    fromPlanName,
    planChangeDirection,
  };
};

export const useCheckoutProration = ({
  displayPayButton,
  checkoutPlanId,
  activeSubscription,
  frequency,
  currentPrice,
  isSubscriptionPending = false,
}: UseCheckoutProrationParams) => {
  const [proration, setProration] = useState<CheckoutProration | null>(null);

  const isChangePlanCheckout = useMemo(() => {
    if (!displayPayButton || !checkoutPlanId || !activeSubscription) {
      return false;
    }

    if (!isPaidActiveSubscription(activeSubscription)) {
      return false;
    }

    const currentPlanId =
      activeSubscription.subscriptionPlanId ??
      activeSubscription.subscriptionPlan?.id;
    if (!currentPlanId) return false;

    if (currentPlanId !== checkoutPlanId) return true;

    const checkoutFreq =
      frequency === SubscriptionFrequency.YEAR ||
      frequency === SubscriptionFrequency.MONTH
        ? frequency
        : null;
    const currentFreq = activeSubscription.frequency;
    return Boolean(checkoutFreq && currentFreq && checkoutFreq !== currentFreq);
  }, [displayPayButton, checkoutPlanId, activeSubscription, frequency]);

  const fromPlanName = useMemo(() => {
    if (proration?.fromPlanName) {
      return proration.fromPlanName;
    }

    const plan = activeSubscription?.subscriptionPlan;
    return plan?.productName || plan?.name || 'Pro';
  }, [activeSubscription?.subscriptionPlan, proration?.fromPlanName]);

  useEffect(() => {
    if (!isChangePlanCheckout || !checkoutPlanId) {
      setProration(null);
      return;
    }

    const freq =
      frequency === SubscriptionFrequency.YEAR ||
      frequency === SubscriptionFrequency.MONTH
        ? frequency
        : null;
    if (!freq) {
      setProration(null);
      return;
    }

    let cancelled = false;
    setProration(null);

    void (async () => {
      try {
        const data = await getChangePlanPreviewRequest({
          subscriptionPlanId: checkoutPlanId,
          frequency: freq,
        });
        if (cancelled) return;
        setProration(mapPreviewToProration(data, freq, currentPrice));
      } catch {
        if (!cancelled) {
          setProration(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isChangePlanCheckout, checkoutPlanId, frequency, currentPrice]);

  const isChangePlanPreviewLoading = isChangePlanCheckout && proration === null;

  const isCheckoutDetailsLoading =
    isSubscriptionPending || isChangePlanPreviewLoading;

  const pricesReady = !isCheckoutDetailsLoading;

  const showProrationCredit =
    isChangePlanCheckout && proration != null && proration.creditAmount > 0;

  const showRemainingCredit =
    isChangePlanCheckout &&
    proration != null &&
    proration.remainingCreditBalance > 0;

  return {
    isUpgradeCheckout: isChangePlanCheckout,
    proration,
    pricesReady,
    isCheckoutDetailsLoading,
    isChangePlanPreviewLoading,
    fromPlanName,
    showProrationCredit,
    showRemainingCredit,
    renewalDateFromPreview: proration?.renewalDate ?? null,
    planChangeDirection: proration?.planChangeDirection ?? null,
  };
};
